import React, { useState, useEffect } from "react";
import {
  getOrders,
  updateOrderStatus,
  getProducts,
  updateProductStock,
  deleteOrder,
} from "../services/adminApi";
import { useNotification } from "../context/NotificationContext";

const Admin = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(null);
  const [newStock, setNewStock] = useState({});
  const [updatingStock, setUpdatingStock] = useState(null);
  const [activeTab, setActiveTab] = useState("orders"); // "orders" or "products"
  const { showNotification } = useNotification();

  useEffect(() => {
    // Load both admin datasets once so order and stock tabs stay in sync.
    const fetchData = async () => {
      try {
        const ordersResponse = await getOrders();
        setOrders(ordersResponse.data);

        const productsResponse = await getProducts();
        setProducts(productsResponse.data);
      } catch (err) {
        console.error("Erreur lors de la récupération des données", err);
      }
    };

    fetchData();
  }, []);

  const handleOrderStatusChange = async (orderId, newStatus) => {
    setLoadingOrder(orderId);
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );
      showNotification(`Statut de la commande mis à jour en "${newStatus}".`, 'success');
    } catch (error) {
      showNotification("Erreur lors de la mise à jour du statut.", 'error');
    } finally {
      setLoadingOrder(null);
    }
  };

  const handleOrderDelete = async (orderId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette commande et restaurer son stock ?")) {
      return;
    }
    setLoadingOrder(orderId);
    try {
      await deleteOrder(orderId);
      setOrders((prevOrders) => prevOrders.filter((order) => order._id !== orderId));
      
      // Refresh products after stock restoration performed by the backend.
      const productsResponse = await getProducts();
      setProducts(productsResponse.data);
      
      showNotification("Commande supprimée et stock restauré.", 'success');
    } catch (error) {
      console.error("Erreur lors de la suppression de la commande :", error);
      showNotification("Erreur lors de la suppression de la commande.", 'error');
    } finally {
      setLoadingOrder(null);
    }
  };

  const handleStockUpdate = async (productId) => {
    const stockValue = newStock[productId];

    if (stockValue === undefined || stockValue === "" || stockValue < 0) {
      showNotification("Veuillez entrer une quantité de stock valide.", 'error');
      return;
    }

    try {
      setUpdatingStock(productId);
      await updateProductStock(productId, Number(stockValue));
      showNotification(`Stock du produit mis à jour à ${stockValue}.`, 'success');

      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product._id === productId ? { ...product, stock: Number(stockValue) } : product
        )
      );
      setNewStock({ ...newStock, [productId]: "" });
    } catch (error) {
      console.error("Erreur lors de la mise à jour du stock :", error);
      showNotification("Échec de la mise à jour du stock.", 'error');
    } finally {
      setUpdatingStock(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "En attente":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "En cours de traitement":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Expédiée":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Console d'administration</h2>
        <p className="text-slate-500 text-sm mt-1">Gérez vos commandes, modifiez vos stocks de produits.</p>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-slate-200 gap-4">
        <button
          onClick={() => setActiveTab("orders")}
          className={`py-2 px-4 font-semibold text-sm transition-all border-b-2 -mb-px ${
            activeTab === "orders"
              ? "border-indigo-600 text-indigo-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Gestion des Commandes ({orders.length})
        </button>
        <button
          onClick={() => setActiveTab("products")}
          className={`py-2 px-4 font-semibold text-sm transition-all border-b-2 -mb-px ${
            activeTab === "products"
              ? "border-indigo-600 text-indigo-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Gestion des Stocks ({products.length})
        </button>
      </div>

      {/* Contenu Commandes */}
      {activeTab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
              <p className="text-slate-500 text-sm">Aucune commande passée pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {orders.map((order) => (
                <div key={order._id} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2.5">
                      <span className="font-mono text-xs text-slate-400">ID: {order._id}</span>
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${getStatusBadge(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-800">
                      Montant : <span className="text-indigo-600 font-bold">{order.total.toFixed(2)} €</span>
                    </div>
                    <div className="text-xs text-slate-400">
                      Date : {new Date(order.createdAt).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                    {order.status === "En attente" && (
                      <button
                        onClick={() => handleOrderStatusChange(order._id, "En cours de traitement")}
                        disabled={loadingOrder === order._id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        Valider la commande
                      </button>
                    )}

                    {order.status === "En cours de traitement" && (
                      <button
                        onClick={() => handleOrderStatusChange(order._id, "Expédiée")}
                        disabled={loadingOrder === order._id}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        Expédier
                      </button>
                    )}

                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="border border-slate-200 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                    >
                      Détails
                    </button>

                    <button
                      onClick={() => handleOrderDelete(order._id)}
                      disabled={loadingOrder === order._id}
                      className="bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Contenu Produits */}
      {activeTab === "products" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Nom</th>
                  <th className="px-6 py-3">Prix</th>
                  <th className="px-6 py-3">Stock Actuel</th>
                  <th className="px-6 py-3">Modifier le Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {products.map((product) => {
                  const isLowStock = product.stock <= 5 && product.stock > 0;
                  const isOutOfStock = product.stock <= 0;

                  return (
                    <tr key={product._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-slate-800">{product.name}</td>
                      <td className="px-6 py-4 text-slate-600 font-medium">{product.price.toFixed(2)} €</td>
                      <td className="px-6 py-4">
                        {isOutOfStock ? (
                          <span className="bg-rose-50 text-rose-600 px-2 py-0.5 rounded-md font-bold text-xs border border-rose-100">
                            Épuisé
                          </span>
                        ) : isLowStock ? (
                          <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded-md font-bold text-xs border border-amber-100">
                            Critique ({product.stock})
                          </span>
                        ) : (
                          <span className="text-slate-700 font-semibold">{product.stock}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="Stock"
                            min="0"
                            value={newStock[product._id] !== undefined ? newStock[product._id] : ""}
                            onChange={(e) =>
                              setNewStock({ ...newStock, [product._id]: e.target.value })
                            }
                            className="border border-slate-200 rounded-lg p-1.5 w-20 text-sm focus:border-indigo-500 focus:outline-none bg-slate-50 focus:bg-white"
                          />
                          <button
                            onClick={() => handleStockUpdate(product._id)}
                            disabled={updatingStock === product._id}
                            className="bg-indigo-650 hover:bg-indigo-700 text-white font-medium text-xs px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                          >
                            Mettre à jour
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fenêtre modale pour afficher les détails de la commande */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto space-y-6">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Détails de la commande</h3>
                <span className="font-mono text-xs text-slate-400">ID: {selectedOrder._id}</span>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${getStatusBadge(selectedOrder.status)}`}>
                {selectedOrder.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
              <div>
                <strong className="block text-xs uppercase tracking-wider text-slate-400 font-bold mb-0.5">Total</strong>
                <span className="text-base font-bold text-indigo-600">{selectedOrder.total.toFixed(2)} €</span>
              </div>
              <div>
                <strong className="block text-xs uppercase tracking-wider text-slate-400 font-bold mb-0.5">Date</strong>
                <span>{new Date(selectedOrder.createdAt).toLocaleString()}</span>
              </div>
            </div>

            {selectedOrder.shippingAddress && (
              <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-700">
                <strong className="block text-xs uppercase tracking-wider text-slate-400 font-bold mb-1.5">Adresse de livraison</strong>
                <p className="font-semibold text-slate-800">{selectedOrder.shippingAddress.street}</p>
                <p>{selectedOrder.shippingAddress.postalCode} {selectedOrder.shippingAddress.city}</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{selectedOrder.shippingAddress.country}</p>
              </div>
            )}

            <div className="space-y-3">
              <strong className="block text-xs uppercase tracking-wider text-slate-400 font-bold">Produits commandés</strong>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg px-4 bg-slate-50/50">
                {selectedOrder.items.map((item, index) => {
                  // Product may be populated or only stored as an id depending on the API response.
                  const product = item.productId;
                  const productName = product && typeof product === "object" ? product.name : "Produit inconnu ou supprimé";
                  const productId = product && typeof product === "object" ? product._id : item.productId;
                  return (
                    <div key={index} className="flex justify-between items-center py-3 text-sm">
                      <div>
                        <p className="font-semibold text-slate-800">{productName}</p>
                        <p className="font-mono text-xs text-slate-400">ID Produit : {productId}</p>
                        <p className="text-xs text-slate-500">Quantité : {item.quantity}</p>
                      </div>
                      <div className="text-right font-semibold text-slate-800">
                        {item.price.toFixed(2)} €
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-100">
              <button
                onClick={() => setSelectedOrder(null)}
                className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
